import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import PartnerList from "../components/partners/PartnerList";
import ProspectList from "../components/partners/ProspectList";
import Button from "../components/common/Button";
import Modal from "../components/common/Modal";
import PartnerForm from "../components/partners/PartnerForm";
import ProspectForm from "../components/partners/ProspectForm";
import { partnerService } from "../services/partners";
import { useAuthStore } from "../store/authStore";
import toast from "react-hot-toast";

const Partners = () => {
  const [activeTab, setActiveTab] = useState("prospects");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [filters, setFilters] = useState({});
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.role === "admin";

  const deletePartner = useMutation({
    mutationFn: (id) => partnerService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast.success("Partner deleted");
    },
    onError: (error) => toast.error(error.response?.data?.message || "Error deleting partner"),
  });

  const deleteProspect = useMutation({
    mutationFn: (id) => partnerService.deleteProspect(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-prospects"] });
      toast.success("Prospect deleted");
    },
    onError: (error) => toast.error(error.response?.data?.message || "Error deleting prospect"),
  });

  const convertProspect = useMutation({
    mutationFn: (id) => partnerService.convertProspect(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-prospects"] });
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast.success("Prospect converted to partner!");
    },
    onError: (error) => toast.error(error.response?.data?.message || "Error converting prospect"),
  });

  const handleEditPartner = (partner) => { setSelectedPartner(partner); setSelectedProspect(null); setIsModalOpen(true); };
  const handleDeletePartner = (id) => { if (window.confirm("Are you sure?")) deletePartner.mutate(id); };
  const handleEditProspect = (prospect) => { setSelectedProspect(prospect); setSelectedPartner(null); setIsModalOpen(true); };
  const handleDeleteProspect = (id) => { if (window.confirm("Are you sure?")) deleteProspect.mutate(id); };
  const handleConvert = (prospect) => { if (window.confirm(`Convert "${prospect.name}" to partner?`)) convertProspect.mutate(prospect._id); };

  const openCreateModal = () => {
    setSelectedPartner(null);
    setSelectedProspect(null);
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setSelectedPartner(null); setSelectedProspect(null); };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Partners</h1>
            <p className="text-sm text-gray-500 mt-1">Your partner details</p>
          </div>
        </div>
        <PartnerList filters={{}} onEdit={null} onDelete={null} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partners</h1>
          <p className="text-sm text-gray-500 mt-1">Manage prospects and partners</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={activeTab === "prospects" ? "primary" : "secondary"} onClick={() => { setActiveTab("prospects"); setFilters({}); }}>
            Prospects
          </Button>
          <Button variant={activeTab === "partners" ? "primary" : "secondary"} onClick={() => { setActiveTab("partners"); setFilters({}); }}>
            Partners
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={activeTab === "prospects" ? "Search prospects..." : "Search partners..."}
              value={filters.search || ""}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-56"
            />
          </div>
          <Button onClick={openCreateModal}>
            {activeTab === "prospects" ? "+ Add Prospect" : "+ Add Partner"}
          </Button>
        </div>
      </div>

      {activeTab === "prospects" ? (
        <ProspectList filters={filters} onEdit={handleEditProspect} onDelete={handleDeleteProspect} onConvert={handleConvert} />
      ) : (
        <PartnerList filters={filters} onEdit={handleEditPartner} onDelete={handleDeletePartner} />
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={
          activeTab === "prospects"
            ? (selectedProspect ? "Edit Prospect" : "Add Prospect")
            : (selectedPartner ? "Edit Partner" : "Add Partner")
        }
      >
        {activeTab === "prospects" ? (
          <ProspectForm prospect={selectedProspect} onSuccess={closeModal} />
        ) : (
          <PartnerForm partner={selectedPartner} onSuccess={closeModal} />
        )}
      </Modal>
    </div>
  );
};

export default Partners;
